import { Button, makeStyles, Typography } from '@material-ui/core';
import { InfoCard } from '@backstage/core-components';
import { Link as RouterLink } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  description: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.6,
  },
  link: {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontWeight: 500,
  },
  buttonRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  outlinedButton: {
    textTransform: 'none',
    fontWeight: 500,
  },
  filledButton: {
    textTransform: 'none',
    fontWeight: 500,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

export const WelcomeCard = () => {
  const classes = useStyles();

  return (
    <InfoCard title="Welcome to Backstage!">
      <Typography variant="body1" className={classes.description}>
        Backstage is the home of your platform — built to make it easier for
        engineers to discover tools created by others. Don't reinvent the wheel,
        search for it.
      </Typography>
      <Typography variant="body1" className={classes.description}>
        Do you wonder why we chose Backstage in the first place?{' '}
        <a
          href="https://backstage.io/docs"
          target="_blank"
          rel="noopener noreferrer"
          className={classes.link}
        >
          Find out more
        </a>{' '}
        about Backstage in TechDocs.
      </Typography>
      <div className={classes.buttonRow}>
        {/*
         * The two buttons deep-link into our own Onboarding Guide
         * TechDocs — one per language. The English version is the
         * default (`onboarding-guide`); the PT-BR version is a
         * 1-for-1 mirror at `onboarding-guide-pt-br`. See
         * `catalog/onboarding/{en,pt-br}/`.
         *
         * Wiring choice — `component={RouterLink} to="..."`:
         *   - Plain `<Button href="...">` would render as `<a>`
         *     and trigger a full page reload, which is jarring
         *     for an in-portal route.
         *   - `Link` from `@backstage/core-components` IS what we
         *     normally use for in-app routing, but it ships its
         *     own `color: inherit`/text-decoration styles that
         *     fight the Button's variant styles when used as
         *     `component={Link}`. Symptom in our case: the
         *     outlined Button rendered as a solid blue block with
         *     no visible label (text colour ended up the same as
         *     the background).
         *   - `Link` from `react-router-dom` is a pure routing
         *     primitive with no styling of its own — it just
         *     calls `useNavigate()` under the hood. Wrapping the
         *     Button with `component={RouterLink}` therefore
         *     gives us SPA navigation while preserving every
         *     Button style (variant, color, padding, label).
         *
         * Visual hierarchy: English is `variant="contained"`
         * (filled, more emphatic) because it's the default; PT-BR
         * is `variant="outlined"` (lighter) as the alternative.
         * Each label is in its own language so the click target
         * matches the experience the user is about to land in
         * (no "Português" labelled button leading to English
         * docs).
         */}
        <Button
          component={RouterLink}
          to="/docs/default/component/onboarding-guide-pt-br"
          variant="outlined"
          color="primary"
          className={classes.outlinedButton}
        >
          Get Started (Português)
        </Button>
        <Button
          component={RouterLink}
          to="/docs/default/component/onboarding-guide"
          variant="contained"
          className={classes.filledButton}
        >
          Get Started (English)
        </Button>
      </div>
    </InfoCard>
  );
};
